import { Image, Palette, Sun } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { usarSesion } from "@/lib/sesion"
import { type Tema, VELO_MAXIMO, VELO_POR_OMISION } from "@/lib/tema"

const OPCIONES: { valor: Tema; etiqueta: string; detalle: string; Icono: typeof Image }[] = [
  {
    valor: "oscuro",
    etiqueta: "Con fotografías",
    detalle: "El puerto de fondo, con el velo que elijas.",
    Icono: Image,
  },
  {
    valor: "claro",
    etiqueta: "Claro",
    detalle: "Fondo liso y claro, sin fotografías.",
    Icono: Sun,
  },
]

/**
 * Cómo se ve el sistema, a gusto de cada quien.
 *
 * ⚠ **El predeterminado es el de las fotografías**, y quien nunca abra esto ve
 * exactamente lo de siempre: el velo arranca en el 30 % que se ajustó viéndolo en
 * producción. El modo claro no es un velo blanco encima de la foto: la quita.
 *
 * ⚠ **Se guarda en ESTE navegador**, no en la cuenta. Es una preferencia de pantalla
 * —el monitor de la oficina y el teléfono en el muelle piden cosas distintas—, y
 * guardarla en el servidor haría que elegir claro en uno lo cambiara en el otro.
 *
 * El panel es opaco (`.panel-flotante`), como todo lo que flota sobre el armazón.
 *
 * ⚠ **Va en un portal, con posición fija.** El encabezado vive dentro de un panel
 * redondeado con `overflow-hidden`, y un panel absoluto quedaría recortado a la
 * altura de la franja. En el portal, además, toma la paleta de la raíz: oscura con
 * fotografías, clara en el modo claro.
 */
export function Apariencia() {
  const tema = usarSesion((e) => e.tema)
  const veloFotos = usarSesion((e) => e.veloFotos)
  const setTema = usarSesion((e) => e.setTema)
  const setVeloFotos = usarSesion((e) => e.setVeloFotos)
  const [abierto, setAbierto] = useState(false)
  /** Dónde se ancla el panel: bajo el botón, alineado a su borde derecho. */
  const [ancla, setAncla] = useState<{ top: number; right: number } | null>(null)
  const caja = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const idPanel = useId()
  const idVelo = useId()

  // Se cierra con Escape y al pulsar fuera: es lo que se espera de un menú, y sin
  // ello se queda tapando el área de trabajo hasta que alguien encuentre el botón.
  useEffect(() => {
    if (!abierto) return
    function alPulsar(evento: PointerEvent) {
      const objetivo = evento.target as Node
      if (!caja.current?.contains(objetivo) && !panel.current?.contains(objetivo)) {
        setAbierto(false)
      }
    }
    function alTeclear(evento: KeyboardEvent) {
      if (evento.key === "Escape") setAbierto(false)
    }
    document.addEventListener("pointerdown", alPulsar)
    document.addEventListener("keydown", alTeclear)
    return () => {
      document.removeEventListener("pointerdown", alPulsar)
      document.removeEventListener("keydown", alTeclear)
    }
  }, [abierto])

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        aria-label="Apariencia del sistema"
        aria-expanded={abierto}
        aria-controls={idPanel}
        onClick={(evento) => {
          const caja = evento.currentTarget.getBoundingClientRect()
          setAncla({ top: caja.bottom + 8, right: window.innerWidth - caja.right })
          setAbierto((v) => !v)
        }}
        className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.85)] toque-cristal foco-cristal"
      >
        <Palette size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>

      {abierto &&
        ancla &&
        createPortal(
          <div
            ref={panel}
            id={idPanel}
            className="panel-flotante fixed z-50 w-[min(18rem,calc(100vw-2rem))] space-y-3 p-3 text-sm"
            style={{ color: "var(--fg)", top: ancla.top, right: Math.max(ancla.right, 16) }}
          >
            <fieldset className="space-y-1">
              <legend className="mb-1 font-semibold">Apariencia</legend>
              {OPCIONES.map(({ valor, etiqueta, detalle, Icono }) => (
                <label
                  key={valor}
                  className="flex cursor-pointer items-start gap-2 rounded-[var(--radius-control)] p-2"
                  style={tema === valor ? { backgroundColor: "var(--primary-suave)" } : undefined}
                >
                  <input
                    type="radio"
                    name="apariencia"
                    checked={tema === valor}
                    onChange={() => setTema(valor)}
                    className="mt-1"
                  />
                  <Icono
                    size={16}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block font-medium">{etiqueta}</span>
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {detalle}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            {/* Sólo con fotografías: en el modo claro no hay nada que oscurecer, y un
              control que no hace nada visible enseña que el sistema no responde. */}
            {tema === "oscuro" && (
              <div className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <label htmlFor={idVelo} className="font-medium">
                    Oscurecer las fotografías
                  </label>
                  <span className="tabular-nums text-xs" style={{ color: "var(--fg-muted)" }}>
                    {Math.round(veloFotos * 100)} %
                  </span>
                </div>
                <input
                  id={idVelo}
                  type="range"
                  min={0}
                  max={VELO_MAXIMO}
                  step={0.05}
                  value={veloFotos}
                  onChange={(e) => setVeloFotos(Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: "var(--primary)" }}
                />
                <button
                  type="button"
                  onClick={() => setVeloFotos(VELO_POR_OMISION)}
                  disabled={veloFotos === VELO_POR_OMISION}
                  className="text-xs underline disabled:no-underline disabled:opacity-50"
                  style={{ color: "var(--fg-muted)" }}
                >
                  Volver al de siempre
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
